import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox, FormControlLabel, MenuItem, Select, InputLabel, FormControl, Stack, Autocomplete, CircularProgress, SelectChangeEvent
} from '@mui/material';

const RULE_TYPES = [
  'Pathologist Assignment',
  'Grossing Tech Assignment',
  'Worklist Routing',
  'Template Assignment',
  'Priority Setting',
  'Other',
];

const PRIORITY_OPTIONS = ['STAT', 'Routine', 'High'];
const THEN_PRIORITY_OPTIONS = ['STAT', 'Routine', 'Deferred'];
const ACTIVATION_SCOPE = ['All Specimens', 'Specific Department', 'Specific Date Range'];

const initialRule = {
  name: '',
  ruleTypes: [] as string[],
  otherRuleType: '',
  specimenClass: '',
  collectionSite: '',
  specimenSource: '',
  orderingPhysician: '',
  partType: '',
  priority: '',
  assignPathologist: '',
  assignGrossingTech: '',
  sendToWorklist: '',
  applyTemplate: '',
  setPriorityLevel: '',
  otherActions: '',
  activationScope: [] as string[],
  dependencies: '',
  createdBy: '',
  dateCreated: '',
  lastReviewedBy: '',
  dateReviewed: '',
};

const Automation: React.FC = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...initialRule });
  const [saving, setSaving] = useState(false);

  // Advanced dropdown state
  const [specimenClasses, setSpecimenClasses] = useState<any[]>([]);
  const [specimenParts, setSpecimenParts] = useState<any[]>([]);
  const [pathologists, setPathologists] = useState<any[]>([]);
  const [grossingTechs, setGrossingTechs] = useState<any[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // Fetch rules from backend
  useEffect(() => {
    setLoading(true);
    fetch('/api/automation-rules', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    })
      .then(res => res.json())
      .then(data => setRules(data))
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
  }, []);

  // Fetch dropdown options
  useEffect(() => {
    setDropdownLoading(true);
    Promise.all([
      fetch('/api/specimen-classes').then(res => res.json()).then(data => Array.isArray(data) ? data : []),
      fetch('/api/specimen-parts').then(res => res.json()).then(data => Array.isArray(data) ? data : []),
      fetch('/api/users?role=pathologist').then(res => res.json()).then(data => Array.isArray(data) ? data : []),
      fetch('/api/users?role=grossing_tech').then(res => res.json()).then(data => Array.isArray(data) ? data : []),
    ]).then(([classes, parts, paths, techs]) => {
      setSpecimenClasses(classes);
      setSpecimenParts(parts);
      setPathologists(paths);
      setGrossingTechs(techs);
    }).catch(() => {
      setSpecimenClasses([]);
      setSpecimenParts([]);
      setPathologists([]);
      setGrossingTechs([]);
    }).finally(() => setDropdownLoading(false));
  }, []);

  const handleOpen = () => {
    setForm({ ...initialRule });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked, value } = e.target;
    if (name === 'ruleTypes') {
      setForm(f => ({
        ...f,
        ruleTypes: checked
          ? [...f.ruleTypes, value]
          : f.ruleTypes.filter((t: string) => t !== value),
      }));
    } else if (name === 'activationScope') {
      setForm(f => ({
        ...f,
        activationScope: checked
          ? [...f.activationScope, value]
          : f.activationScope.filter((t: string) => t !== value),
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const name = e.target.name as string;
    setForm(f => ({ ...f, [name]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/automation-rules', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save rule');
      const saved = await res.json();
      setRules(prev => [saved, ...prev]);
      setOpen(false);
    } catch (e) {
      alert('Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Automation Rules</Typography>
      <Divider sx={{ mb: 2 }} />
      <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={handleOpen}>Add New Rule</Button>
      <Paper sx={{ p: 2, minHeight: 200 }}>
        {loading ? <CircularProgress /> : rules.length === 0 ? (
          <Typography variant="body1" color="text.secondary">
            No rules defined yet. Click "Add New Rule" to create your first automation rule.
          </Typography>
        ) : (
          <Box>
            {rules.map(rule => (
              <Paper key={rule.id} sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1"><b>{rule.name}</b></Typography>
                <Typography variant="body2">Type: {rule.ruleTypes?.join(', ')} {rule.otherRuleType && `(Other: ${rule.otherRuleType})`}</Typography>
                <Typography variant="body2">Specimen Class: {rule.specimenClass}</Typography>
                <Typography variant="body2">Priority: {rule.priority}</Typography>
                <Typography variant="body2">THEN: Assign Pathologist: {rule.assignPathologist}, Grossing Tech: {rule.assignGrossingTech}, Worklist: {rule.sendToWorklist}, Template: {rule.applyTemplate}, Priority: {rule.setPriorityLevel}</Typography>
                <Typography variant="body2">Activation: {rule.activationScope?.join(', ')}</Typography>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Routing Rule</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Rule ID / Name" name="name" value={form.name} onChange={handleChange} fullWidth required />
          <Box>
            <Typography variant="subtitle2">Rule Type:</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {RULE_TYPES.map(type => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={form.ruleTypes.includes(type)}
                      onChange={handleCheckboxChange}
                      name="ruleTypes"
                      value={type}
                    />
                  }
                  label={type}
                />
              ))}
              {form.ruleTypes.includes('Other') && (
                <TextField
                  label="Other Type"
                  name="otherRuleType"
                  value={form.otherRuleType}
                  onChange={handleChange}
                  size="small"
                  sx={{ ml: 2, width: 200 }}
                />
              )}
            </Stack>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">IF Conditions:</Typography>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Specimen Class</InputLabel>
              <Select
                name="specimenClass"
                value={form.specimenClass}
                onChange={handleSelectChange}
                label="Specimen Class"
                disabled={dropdownLoading}
              >
                <MenuItem value="">Select</MenuItem>
                {specimenClasses.map(cls => (
                  <MenuItem key={cls.id} value={cls.label}>{cls.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Collection Site" name="collectionSite" value={form.collectionSite} onChange={handleChange} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Specimen Source / Site</InputLabel>
              <Select
                name="specimenSource"
                value={form.specimenSource}
                onChange={handleSelectChange}
                label="Specimen Source / Site"
                disabled={dropdownLoading}
              >
                <MenuItem value="">Select</MenuItem>
                {specimenParts.map(part => (
                  <MenuItem key={part.id} value={part.part_name}>{part.part_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Ordering Physician" name="orderingPhysician" value={form.orderingPhysician} onChange={handleChange} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Part Type or Label" name="partType" value={form.partType} onChange={handleChange} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Priority (if known)</InputLabel>
              <Select
                name="priority"
                value={form.priority}
                onChange={handleSelectChange}
                label="Priority (if known)"
              >
                <MenuItem value="">Select</MenuItem>
                {PRIORITY_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">THEN Actions:</Typography>
          <FormControl fullWidth>
            <InputLabel>Assign to Pathologist</InputLabel>
            <Select
              name="assignPathologist"
              value={form.assignPathologist}
              onChange={handleSelectChange}
              label="Assign to Pathologist"
              disabled={dropdownLoading}
            >
              <MenuItem value="">Select</MenuItem>
              {pathologists.map(user => (
                <MenuItem key={user.id} value={user.name}>{user.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Assign Grossing Tech</InputLabel>
            <Select
              name="assignGrossingTech"
              value={form.assignGrossingTech}
              onChange={handleSelectChange}
              label="Assign Grossing Tech"
              disabled={dropdownLoading}
            >
              <MenuItem value="">Select</MenuItem>
              {grossingTechs.map(user => (
                <MenuItem key={user.id} value={user.name}>{user.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Send to Worklist" name="sendToWorklist" value={form.sendToWorklist} onChange={handleChange} fullWidth />
          <TextField label="Apply Template" name="applyTemplate" value={form.applyTemplate} onChange={handleChange} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Set Priority Level</InputLabel>
            <Select
              name="setPriorityLevel"
              value={form.setPriorityLevel}
              onChange={handleSelectChange}
              label="Set Priority Level"
            >
              <MenuItem value="">Select</MenuItem>
              {THEN_PRIORITY_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Other Actions" name="otherActions" value={form.otherActions} onChange={handleChange} fullWidth />
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">Activation Scope:</Typography>
          <Stack direction="row" spacing={2}>
            {ACTIVATION_SCOPE.map(scope => (
              <FormControlLabel
                key={scope}
                control={
                  <Checkbox
                    checked={form.activationScope.includes(scope)}
                    onChange={handleCheckboxChange}
                    name="activationScope"
                    value={scope}
                  />
                }
                label={scope}
              />
            ))}
          </Stack>
          <TextField label="Dependencies / Notes" name="dependencies" value={form.dependencies} onChange={handleChange} fullWidth multiline minRows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label="Created By" name="createdBy" value={form.createdBy} onChange={handleChange} fullWidth />
            <TextField label="Date Created" name="dateCreated" value={form.dateCreated} onChange={handleChange} fullWidth type="date" InputLabelProps={{ shrink: true }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Last Reviewed By" name="lastReviewedBy" value={form.lastReviewedBy} onChange={handleChange} fullWidth />
            <TextField label="Date Reviewed" name="dateReviewed" value={form.dateReviewed} onChange={handleChange} fullWidth type="date" InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Rule'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Automation; 